/**
 * What a failed call to OpenCode on this computer means to the person,
 * decided once for every call the local runtime makes and for the plugin's
 * `start()`. Each failure becomes a `SessionFailure` with plain words, the
 * status the reads that retry by status use, and the kind its recovery is
 * read from (`localFailure`):
 * - a server the page cannot reach or that is not running (503), a request
 *   it took and never finished answering (504), and any failure of the
 *   server's or the plugin's own (5xx): Retry starts the server again, then
 *   does what its place offers: an action's toast and the session page read
 *   the session again, a failed turn sends again and a failed create creates
 *   again;
 * - the server's refusal of the request (400, 403, 409): its answer, which a
 *   Retry would not change;
 * - a session OpenCode no longer has (404): the sidebar's list of sessions on
 *   this computer is read again, and nothing is retried.
 * The method, path, host or hash a message carried goes to the client log,
 * never to the person.
 */
import type { SessionFailureKind } from '../request-error';

import { SessionFailure } from '../request-error';

import { clientLogger } from '@/lib/client-logger';
import { LocalAgentError } from '@/lib/code-sessions/local-agent/errors';
import {
  OpenCodeHttpError,
  OpenCodeTimeoutError,
} from '@/lib/code-sessions/local-agent/opencode-api';

export const LOST_CONNECTION_MESSAGE = 'Lost the connection to the agent on this computer';

const FAILED_TO_ANSWER_MESSAGE = 'The agent on this computer failed to answer';

export const SESSION_GONE_MESSAGE = 'This session no longer exists on this computer';

/**
 * Why a session on the person's computer does not load where nothing could run it: a page with no
 * desktop plugin, or a computer where This computer was never used. It says where the session
 * runs, so nobody reads their own computer as the problem.
 */
export const SESSION_ELSEWHERE_MESSAGE =
  'This session runs in the cubic desktop app on the computer that started it';

/** Why a send failed when a read after it gave up lists no prompt under its id (`unsentBefore`). */
export const NEVER_GOT_MESSAGE = 'The agent on this computer never got this message';

/**
 * The statuses that are the server's refusal, which a Retry would not
 * change. A 401 keeps Retry: the start mends a stale password, since the
 * plugin hands the page the running server's pair, and a sign-in to cubic is
 * followed by one.
 */
const REFUSED_STATUSES: ReadonlySet<number> = new Set([400, 403, 409]);

/**
 * What a status means for the person: a session OpenCode no longer has (404); a call the server
 * took and never finished answering (504, as the client's deadline reads it), which may still have
 * done its work; the server's own answer, which a Retry would not change; or a failure a Retry,
 * which starts the server again before it reads the session, sends or creates again, may mend.
 */
function kindOf(status: number): SessionFailureKind {
  if (status === 404) {
    return 'gone';
  }
  if (status === 504) {
    return 'unconfirmed';
  }
  return REFUSED_STATUSES.has(status) ? 'refused' : 'lost';
}

export function localFailure(message: string, status: number): SessionFailure {
  return new SessionFailure(message, status, kindOf(status));
}

/** The failure of a session nothing here could run (`SESSION_ELSEWHERE_MESSAGE`). */
export function sessionElsewhere(): SessionFailure {
  return new SessionFailure(SESSION_ELSEWHERE_MESSAGE, 400, 'elsewhere');
}

/** An item under a session's route: a queued message (`inbox`) or a question (`form`). */
const ITEM_PATH = /^\/api\/session\/[^/]+\/(inbox|form)\/[^/]+/;

/** The server's own answer, by its status and the route it answered. */
function serverFailure(error: OpenCodeHttpError): SessionFailure {
  const { status } = error;
  if (status === 404) {
    // An item that is gone is the server's answer to this action alone; a session that is gone
    // is gone for every action.
    const item = ITEM_PATH.exec(error.request.path)?.[1];
    if (item === 'inbox') {
      return localFailure('This message is no longer queued on this computer', 409);
    }
    if (item === 'form') {
      return localFailure('This question is no longer open on this computer', 409);
    }
    return localFailure(SESSION_GONE_MESSAGE, 404);
  }
  if (status === 401) {
    // The pair the page holds is stale, as after someone else restarted the server: Retry
    // starts it again and hands the page the new pair.
    return localFailure(
      'The agent on this computer did not accept the password this page holds',
      401,
    );
  }
  if (status >= 500) {
    return localFailure(FAILED_TO_ANSWER_MESSAGE, status);
  }
  return localFailure('The agent on this computer refused this request', status);
}

/** The plugin's failure, by its kind: the launch request, the install, the start, or its own refusal. */
function pluginFailure(error: LocalAgentError): SessionFailure {
  switch (error.kind) {
    case 'refused':
      // The client's own refusal, worded for the page already, such as an answer a request
      // does not take.
      return localFailure(error.message, 400);
    case 'server-not-running':
      return localFailure('The agent is not running on this computer', 503);
    case 'http':
      // cubic answered the plugin's launch request with a failure.
      if (error.status === 401) {
        return localFailure('Sign in to cubic again to use this computer', 401);
      }
      if (error.status === 403) {
        return localFailure('cubic did not allow code sessions for this account', 403);
      }
      return localFailure('cubic could not start the agent on this computer', 502);
    case 'install':
      return localFailure('The agent could not be installed on this computer', 500);
    // A kind a later plugin adds reads as a failed start, which this page knows how to word.
    case 'failed':
    default:
      return localFailure('The agent could not start on this computer', error.status ?? 500);
  }
}

/**
 * The page's reading of a failed local call, or null for an error that is
 * not one of the call's own, such as an aborted read. One already read
 * passes through as it is. The failure's detail goes to the client log.
 */
export function toLocalRequestError(error: unknown): SessionFailure | null {
  if (error instanceof SessionFailure) {
    return error;
  }
  let failure: SessionFailure;
  let detail: Record<string, unknown>;
  if (error instanceof LocalAgentError) {
    failure = pluginFailure(error);
    detail = { kind: error.kind, status: error.status, message: error.message };
  } else if (error instanceof OpenCodeHttpError) {
    failure = serverFailure(error);
    detail = { method: error.request.method, path: error.request.path, status: error.status };
  } else if (error instanceof OpenCodeTimeoutError) {
    // The server took the request and never finished answering it, so the client gave up.
    failure = localFailure(FAILED_TO_ANSWER_MESSAGE, 504);
    detail = { method: error.request.method, path: error.request.path, timeoutMs: error.timeoutMs };
  } else if (error instanceof TypeError) {
    // The browser's own failure to reach the server: the request never got an answer.
    failure = localFailure(LOST_CONNECTION_MESSAGE, 503);
    detail = { message: error.message };
  } else {
    return null;
  }
  clientLogger.warn(`OpenCode on this computer: ${failure.message}`, detail);
  return failure;
}
